// app/providers.tsx
'use client'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { env } from '~/config/env'
import { useEffect } from 'react'
import { useRouter } from 'next/router'
import {
    isPosthogTrackedPath,
    POSTHOG_SESSION_RECORDING_OPTIONS,
    updatePosthogSessionRecording,
} from './posthog-navigation'

export function PosthogProvider({
    children,
    bootstrapData
}: {
    children: React.ReactNode
    bootstrapData: any
}) {
    const router = useRouter()

    useEffect(() => {
        let recordingOnTrackedPath = isPosthogTrackedPath(window.location.pathname)

        posthog.init(env.posthog.key!, {
            api_host: env.posthog.host,
            person_profiles: 'identified_only',

            defaults: '2026-01-30',
            capture_pageview: 'history_change',
            capture_pageleave: 'if_capture_pageview',
            session_recording: POSTHOG_SESSION_RECORDING_OPTIONS,
            disable_session_recording: !recordingOnTrackedPath,
            mask_personal_data_properties: true,
            custom_personal_data_properties: ['token', 'tokenId'],

            before_send: (event) => {
                if (
                    event &&
                    (event.event === '$pageview' || event.event === '$pageleave') &&
                    !isPosthogTrackedPath(window.location.pathname)
                ) {
                    return null
                }

                return event
            },
            loaded: (posthog) => {
                if (process.env.NODE_ENV === 'development') {
                    posthog.debug()
                }
            },
            bootstrap: bootstrapData,
        })

        const handleRouteChangeStart = (url: string) => {
            const nextPathname = new URL(url, window.location.origin).pathname

            if (recordingOnTrackedPath && !isPosthogTrackedPath(nextPathname)) {
                posthog.stopSessionRecording()
                recordingOnTrackedPath = false
            }
        }

        const handleRouteChangeComplete = (url: string) => {
            recordingOnTrackedPath = updatePosthogSessionRecording(
                posthog,
                new URL(url, window.location.origin).pathname,
                recordingOnTrackedPath
            )
        }

        const handleRouteChangeError = () => {
            recordingOnTrackedPath = updatePosthogSessionRecording(
                posthog,
                window.location.pathname,
                recordingOnTrackedPath
            )
        }

        router.events.on('routeChangeStart', handleRouteChangeStart)
        router.events.on('routeChangeComplete', handleRouteChangeComplete)
        router.events.on('routeChangeError', handleRouteChangeError)

        return () => {
            router.events.off('routeChangeStart', handleRouteChangeStart)
            router.events.off('routeChangeComplete', handleRouteChangeComplete)
            router.events.off('routeChangeError', handleRouteChangeError)
        }
    }, [router.events])

    return <PostHogProvider client={posthog}>{children}</PostHogProvider>
}
